
import React, { useState, useEffect } from 'react';
import { X, Send, Truck, Building2, MapPin, CheckCircle2, Phone, Mail, User, Briefcase, MessageSquare, Clock, Globe } from 'lucide-react';
import { useData } from '../context/DataContext';
import { sendFormEmail } from '../utils/emailService';

const QuoteModal: React.FC = () => {
  const { isQuoteModalOpen, closeQuoteModal, settings, addInquiry, quoteModalType } = useData();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    businessType: 'Logistics & Transport',
    location: 'Dar es Salaam, TZ',
    message: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isQuoteModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isQuoteModalOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Full name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Invalid email format';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!form.company.trim()) newErrors.company = 'Company name is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Prevent "Enter" from submitting automatically
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLElement;
      // Allow Enter in Textarea for new lines
      if (target.tagName === 'TEXTAREA') {
        return;
      }
      // Allow Enter on Buttons (accessibility)
      if (target.tagName === 'BUTTON') {
        return;
      }

      // Prevent default behavior (form submission) for everything else
      e.preventDefault();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    // Construct message
    const locationInfo = `Location: ${form.location}`;
    const businessInfo = `Business: ${form.company} (${form.businessType})`;
    const userMessage = form.message ? `\nDetails: ${form.message}` : '';
    const fullMessage = `Quote Request.\n${businessInfo}\n${locationInfo}${userMessage}`;

    const formData = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      message: fullMessage,
      type: quoteModalType === 'financing' ? 'Financing Application' : 'Quote Request'
    };

    // STEP 1: Save to Database FIRST (guaranteed)
    addInquiry({
      id: Math.random().toString(36).substr(2, 9),
      name: form.name,
      email: form.email,
      phone: form.phone,
      message: fullMessage,
      type: 'Quote',
      status: 'New',
      date: new Date().toISOString()
    });

    // STEP 2: Show success IMMEDIATELY
    setIsSubmitting(false);
    setIsSuccess(true);
    setTimeout(() => {
      closeQuoteModal();
      setTimeout(() => {
        setIsSuccess(false);
        setForm({
          name: '', email: '', phone: '', company: '',
          businessType: 'Logistics & Transport',
          location: 'Dar es Salaam, TZ', message: ''
        });
      }, 500);
    }, 3500);

    // STEP 3: Try email in background (non-blocking)
    sendFormEmail(formData, 'quote');
  };



  const inputClass = (field: string) => `
    w-full border-2 p-3 rounded-sm outline-none transition-all text-gray-900 text-sm md:text-base
    ${errors[field] ? 'border-red-300 bg-red-50 focus:border-red-500' : 'border-gray-100 bg-gray-50 focus:border-automax-red focus:bg-white'}
  `;

  if (!isQuoteModalOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6 bg-black/80 backdrop-blur-md">
      <div className="absolute inset-0" onClick={closeQuoteModal}></div>

      <div className="relative bg-white w-full max-w-5xl h-[90vh] md:h-auto md:max-h-[90vh] rounded-sm overflow-hidden shadow-2xl flex flex-col md:flex-row">
        {/* Info Sidebar */}
        <div className="w-full md:w-1/3 bg-neutral-900 p-6 md:p-10 flex flex-col justify-between text-white border-r border-white/5 relative overflow-hidden flex-shrink-0">
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              {settings.logoUrl ? (
                <img
                  src={settings.logoUrl}
                  alt={settings.siteName}
                  className="h-10 w-auto object-contain brightness-0 invert"
                />
              ) : (
                <>
                  <div className="p-1.5 rounded bg-automax-red transform skew-x-[-10deg]">
                    <Truck size={24} className="text-white skew-x-[10deg]" />
                  </div>
                  <span className="font-display text-3xl font-bold uppercase tracking-widest">Automax</span>
                </>
              )}
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-800 text-automax-red flex-shrink-0 border border-gray-700">
                  <Clock size={20} />
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Fast Response</span>
                  <p className="text-sm text-gray-300">Quotes provided within 24 hours.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-800 text-automax-red flex-shrink-0 border border-gray-700">
                  <Briefcase size={20} />
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Expert Advice</span>
                  <p className="text-sm text-gray-300">Consultation for fleet requirements.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-800 text-automax-red flex-shrink-0 border border-gray-700">
                  <Globe size={20} />
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Regional Delivery</span>
                  <p className="text-sm text-gray-300">Serving Tanzania, Kenya, Uganda & more.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:block space-y-4 pt-10 border-t border-white/10 mt-10">
            <div className="flex items-center gap-3 text-sm font-medium text-gray-400">
              <Phone size={16} className="text-automax-red" /> {settings.phoneNumber}
            </div>
            <div className="flex items-center gap-3 text-sm font-medium text-gray-400">
              <Mail size={16} className="text-automax-red" /> {settings.email}
            </div>
          </div>

          <Truck className="absolute -bottom-10 -right-10 text-white/5 w-64 h-64 pointer-events-none" />
        </div>

        {/* Form Area */}
        <div className="flex-1 p-6 md:p-10 bg-white flex flex-col relative overflow-y-auto">
          <button
            onClick={closeQuoteModal}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors bg-gray-50 p-2 rounded-full hover:bg-gray-100 z-10"
          >
            <X size={24} />
          </button>

          {isSuccess ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
              <div
                className="w-24 h-24 bg-green-50 text-green-600 rounded-full flex items-center justify-center mb-8 border-4 border-green-100"
              >
                <CheckCircle2 size={56} />
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 uppercase mb-4 tracking-tight">Request Received!</h2>
              <p className="text-gray-600 max-w-sm mx-auto leading-relaxed">
                Thank you, <span className="font-bold text-gray-900">{form.name.split(' ')[0]}</span>. Our commercial sales experts are reviewing your requirements and will contact you shortly.
              </p>
              <div
                className="h-1 bg-green-500 absolute bottom-0 left-0 w-full"
              />
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 uppercase tracking-tight">
                  {quoteModalType === 'financing' ? 'Financing Application' : 'Get Free Quote'}
                </h2>
                <div className="h-1.5 w-20 bg-automax-red mt-3"></div>
                <p className="text-gray-500 mt-4 text-sm md:text-base font-medium">Please fill out the form below to receive a custom proposal.</p>
              </div>

              <form
                onSubmit={handleSubmit}
                onKeyDown={handleKeyDown}
                className="flex-1 flex flex-col space-y-6"
              >
                {/* Personal Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2 border-b border-gray-100 pb-2">
                    <User size={16} className="text-automax-red" />
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Contact Details</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-700 mb-1 ml-1">Full Name *</label>
                      <input
                        required
                        type="text"
                        value={form.name}
                        onChange={e => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: '' }); }}
                        className={inputClass('name')}
                        placeholder="John Doe"
                      />
                      {errors.name && <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 ml-1">Email Address *</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={e => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: '' }); }}
                        className={inputClass('email')}
                        placeholder="john@example.com"
                      />
                      {errors.email && <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 ml-1">Phone Number *</label>
                      <input
                        required
                        type="tel"
                        value={form.phone}
                        onChange={e => { setForm({ ...form, phone: e.target.value }); if (errors.phone) setErrors({ ...errors, phone: '' }); }}
                        className={inputClass('phone')}
                        placeholder="+255..."
                      />
                      {errors.phone && <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{errors.phone}</p>}
                    </div>
                  </div>
                </div>

                {/* Business Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2 border-b border-gray-100 pb-2 pt-2">
                    <Building2 size={16} className="text-automax-red" />
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Business Profile</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-700 mb-1 ml-1">Company Name *</label>
                      <input
                        type="text"
                        required
                        value={form.company}
                        onChange={e => { setForm({ ...form, company: e.target.value }); if (errors.company) setErrors({ ...errors, company: '' }); }}
                        className={inputClass('company')}
                        placeholder="e.g. Acme Logistics Ltd"
                      />
                      {errors.company && <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{errors.company}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 ml-1">Business Sector</label>
                      <select
                        value={form.businessType}
                        onChange={e => setForm({ ...form, businessType: e.target.value })}
                        className={`${inputClass('businessType')} appearance-none`}
                      >
                        <option>Logistics & Transport</option>
                        <option>Construction & Mining</option>
                        <option>Agriculture & Farming</option>
                        <option>Manufacturing & FMCG</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 ml-1">Region</label>
                      <select
                        value={form.location}
                        onChange={e => setForm({ ...form, location: e.target.value })}
                        className={`${inputClass('location')} appearance-none`}
                      >
                        <option>Dar es Salaam, TZ</option>
                        <option>Arusha, TZ</option>
                        <option>Mwanza, TZ</option>
                        <option>Nairobi, KE</option>
                        <option>Kampala, UG</option>
                        <option>Kigali, RW</option>
                        <option>Mombasa, KE</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2 border-b border-gray-100 pb-2 pt-2">
                    <MessageSquare size={16} className="text-automax-red" />
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Requirements</span>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1 ml-1">More Information</label>
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      className={`${inputClass('message')} resize-none`}
                      placeholder="Please specify truck models, quantity, or financing needs..."
                    />
                  </div>
                </div>

                <div className="pt-6 mt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-automax-red text-white py-4 font-bold uppercase text-sm tracking-widest rounded-sm hover:bg-red-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-red-900/20 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> Processing...</>
                    ) : (
                      <><Send size={18} /> {quoteModalType === 'financing' ? 'Submit Application' : 'Send Quote Request'}</>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
